import { AmazonS3Dataset } from 'app/model/dataset/amazon-s3.dataset';
import { FilePath } from 'app/model/dataset/file-path';
import { FileTypeDataset } from 'app/model/dataset/file-type.dataset';
import { IPartition, getPathExpression } from 'app/components/copy-wizard/dataset/dataset-partition-by/copy-wizard.utils';
import { AdlsCompileResultDataset } from 'app/interfaces/IScriptCompilation';
import { Metamodel } from 'app/model/base/model';
import { AzureDataLakeStoreCosmosStructuredStreamFileDataset } from '../../model/dataset/azure-data-lake-store-cosmos-structured-stream.dataset';
import { Location } from 'app/model/dataset/location/location';
import { Owned } from 'app/model/base/named';
import { FormatDatasetV2 } from 'app/model/dataset/dataset.v2';
import { AdobeExperiencePlatformObjectDataset } from 'app/model/dataset/adobe-experience-platform.dataset';
import { ILinkedServiceTypeMetadata, ILocationPattern } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { stringIsNullOrEmpty } from './string.util';
import { AzureDataLakeStoreGen2CosmosStructuredStreamFileDataset } from 'app/model/dataset/azure-data-lake-store-gen2-cosmos-structured-stream.dataset';
import { AzureBlobStorageLinkedService, Constants, LinkedService, ModelConstants } from 'shell-models-lib';
import { extractDsrPathFromRawJson } from 'app/utils/powerqueryDsrUtils';

export function populateDatasetFromFilePath(filePath: FilePath, entity: FileTypeDataset, partitions?: IPartition): void {
    if (!(entity instanceof FormatDatasetV2)) {
        if (entity instanceof AdobeExperiencePlatformObjectDataset) {
            entity.datasetId = filePath.fileName;
        } else {
            setPath(filePath, entity, partitions);
        }
        if (entity instanceof AmazonS3Dataset) {
            entity.bucketName = partitions == null ? filePath.container : getPathExpression(filePath.container, partitions);
        }
        entity.wildcardPath = undefined;
    } else {
        // v2 dataset, filename folderpath are in dataset.location
        const locationModel = Metamodel.models().find((model) => Location.MODEL.isAncestor(model) && model.tag('linkedServiceModel') === Metamodel.modelOf(entity.linkedService));
        if (locationModel != null) {
            const location = Owned.create<Location>(locationModel, entity);
            location.setLocation(filePath);
            entity.location = location;
        }
    }
}

export function populateCosmosStructuredStreamFromFilePath(filePath: FilePath, entity: AzureDataLakeStoreCosmosStructuredStreamFileDataset
    | AzureDataLakeStoreGen2CosmosStructuredStreamFileDataset, partitions?: IPartition): void {
    if (entity instanceof AzureDataLakeStoreGen2CosmosStructuredStreamFileDataset) {
        const slices = filePath.folderPath.split('/');
        const fileSystem = slices && slices.length > 0 ? slices[0] : undefined;
        const folderPath = slices && slices.length > 1 ? slices.slice(1).join('/') : undefined;
        entity.fileSystem = partitions == null ? fileSystem : getPathExpression(fileSystem, partitions);
        entity.folderPath = partitions == null ? folderPath : getPathExpression(folderPath, partitions);
        entity.fileName = partitions == null ? filePath.fileName : getPathExpression(filePath.fileName, partitions);
    } else {
        setPath(filePath, entity, partitions);
    }
}

export function populateFilePathFromDataset(filePath: FilePath, entity: FileTypeDataset): void {
    if (!(entity instanceof FormatDatasetV2)) {
        if (entity instanceof AdobeExperiencePlatformObjectDataset) {
            filePath.fileName = entity.datasetId;
            filePath.isFolder = true;
        } else {
            filePath.fileName = entity.fileName;
            filePath.folderPath = entity.folderPath;
            if (entity instanceof AmazonS3Dataset) {
                filePath.container = entity.bucketName;
            }
            filePath.isFolder = !entity.fileName;
        }
        filePath.path = filePath.getFullPath();
    } else {
        // v2 dataset, filename folderpath are in dataset.location
        if (entity && entity.location != null) {
            filePath = entity.location.getLocation();
        }
    }
}

export function getAdlsDatasetFullPath(dataset: AdlsCompileResultDataset): string {
    return [dataset.folderPath || Metamodel.modelOf(dataset).attribute('folderPath').templateValue(dataset),
    dataset.fileName || Metamodel.modelOf(dataset).attribute('fileName').templateValue(dataset)].filter((value) => value).join('/');
}

function setPath(filePath: FilePath, entity: AzureDataLakeStoreCosmosStructuredStreamFileDataset | AzureDataLakeStoreGen2CosmosStructuredStreamFileDataset
    | FileTypeDataset, partitions?: IPartition): void {
    entity.folderPath = partitions == null ? filePath.folderPath : getPathExpression(filePath.folderPath, partitions);
    entity.fileName = partitions == null ? filePath.fileName : getPathExpression(filePath.fileName, partitions);
}

export function parseFileLocation(path: string, patterns: ILocationPattern): { [key: string]: string } {
    const result: { [key: string]: string } = {};
    Object.keys(patterns).forEach((key) => {
        const ptn = patterns[key];
        const regex = new RegExp(ptn);
        const match = regex.exec(path);
        if (match != null && match.length > 1) {
            result[key] = match[1];
        }
    });
    return result;
}

export function resolveFileLocationExpressionByPartition(pathObj: { [key: string]: string }, partitions: IPartition): void {
    for (const key in pathObj) {
        if (pathObj[key] != null) {
            pathObj[key] = getPathExpression(pathObj[key], partitions);
        }
    }
}

// extract non empty or null folder path properties for test connection
export function filterOutEmptyPathProperties(testConnectionFilePathProperties: { [key: string]: string }, excludeFolderPath: boolean = false): void {
    if (testConnectionFilePathProperties != null) {
        Object.keys(testConnectionFilePathProperties).forEach((key) => {
            if (key === 'fileName' || key === 'type' || stringIsNullOrEmpty(testConnectionFilePathProperties[key])
                || (excludeFolderPath && key === 'folderPath')) {
                delete testConnectionFilePathProperties[key];
            }
        });
    }
}

export function extractFolderPathFromConnection(linkedService: LinkedService, linkedServiceMetadata: ILinkedServiceTypeMetadata): { [key: string]: string } {
    if (linkedService == null) {
        return null;
    }
    if (linkedService instanceof AzureBlobStorageLinkedService) {
        return {
            container: linkedService.getContainerNameInUri() || ''
        };
    } else {
        if (!linkedService.raw || !JSON.parse(linkedService.raw)?.properties?.datasourceReference) {
            return null;
        }
        const connectionUri = JSON.parse(JSON.parse(linkedService.raw).properties.datasourceReference).path || '';
        const matchedResult = new RegExp(Constants.generalConnectionUriPattern).exec(
            connectionUri
        );
        const folderPath = matchedResult?.[1];
        if (folderPath) {
            const pathProps = parseFileLocation(folderPath, linkedServiceMetadata.locationPattern);
            let container = '';
            let folder = '';
            for(const prop in pathProps) {
                if (prop !== 'fileName' && prop !== 'folderPath') {
                    container = pathProps[prop];
                } else if (prop === 'folderPath') {
                    folder= pathProps[prop];
                }
            }
            return {
                container,
                folder
            };
        }
    }
    return null;
}

/**
 * Extracts a bucket name from the connection URL stored in the linked service's data source reference.
 * Supported examples:
 * - `https://my-bucket.s3.us-east-1.amazonaws.com/output` -> `my-bucket`
 * - `https://s3.us-east-1.amazonaws.com/my-bucket/output` -> `my-bucket`
 * - `https://storage.example.com/my-bucket/output` -> `my-bucket` (S3-compatible path style)
 * Returns an empty string when the linked service or connection URL is unsupported or malformed.
 */
export function getBucketNameFromConnectionUrl(linkedService: LinkedService | null | undefined): string {
    const linkedServiceType = linkedService?.getLinkedServiceType();
    if (linkedServiceType !== ModelConstants.amazonS3LinkedService
        && linkedServiceType !== ModelConstants.amazonS3CompatibleLinkedService) {
        return '';
    }

    const connectionUrl = extractDsrPathFromRawJson(linkedService.raw);
    if (stringIsNullOrEmpty(connectionUrl)) {
        return '';
    }

    try {
        const parsedUrl = new URL(connectionUrl);
        if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
            return '';
        }

        // In virtual-hosted URLs the path is a key prefix, so inspect the hostname before path-style parsing.
        if (parsedUrl.hostname.endsWith('.amazonaws.com') || parsedUrl.hostname.endsWith('.amazonaws.com.cn')) {
            const labels = parsedUrl.hostname.split('.');
            const s3LabelIndex = labels.reduce(
                (index, label, currentIndex) => /^s3(?:-|$)/.test(label) ? currentIndex : index,
                -1
            );
            if (s3LabelIndex > 0) {
                return labels.slice(0, s3LabelIndex).join('.');
            }
        }

        const firstPathSegment = parsedUrl.pathname.split('/').find((segment) => segment.length > 0);
        if (!stringIsNullOrEmpty(firstPathSegment)) {
            return firstPathSegment;
        }
    } catch {
        return '';
    }

    return '';
}
